import './RulesScreen.scss';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, MenuItem, Input } from '@mui/material';
import ComparisonSelector from 'src/component/UI/ComparisonSelector';
import DeleteIcon from '@mui/icons-material/DeleteSharp';
import Button from 'src/component/UI/Button';
import PropTypes from 'prop-types';
import { addRuleCondition, updateRuleCondition, deleteRuleCondition } from 'src/module/rule/action';
import { Map } from 'immutable';
import { isNumberString } from 'class-validator';
import { getTemperatureUnit } from 'src/module/authentication/selector';
import { celsiusToFahrenheitConvertor, fahrenheitToCelsiusConvertor } from 'src/utils/utils';
import { useTranslation } from 'src/i18n';

export default function Condition (props) {
  const { condition, ruleId, conditionTypeOptions } = props;
  const _conditionOptions = props.conditionOptions;

  // Convert Immutable List to array and filter out null/undefined
  const conditionTypeOptionsArray = conditionTypeOptions?.toJS ? conditionTypeOptions.toJS() : (Array.isArray(conditionTypeOptions) ? conditionTypeOptions : []);
  const validConditionTypeOptions = (conditionTypeOptionsArray || []).filter(opt => opt != null && opt.value != null);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const temperatureUnit = useSelector(getTemperatureUnit);

  const initialLabel = condition?.label ?? '';
  const initialCompare = condition?.comparison ?? '=';
  const initialValue = condition?.value ?? '';

  const [label, setLabel] = React.useState(initialLabel);
  const [compare, setCompare] = React.useState(initialCompare);
  const [value, setValue] = React.useState(initialValue);

  const [conditionOption, setConditionOption] = React.useState(
    (_conditionOptions || []).filter(opt => opt != null).find(opt => opt?.value === initialLabel)
  );

  useEffect(() => {
    const nextLabel = condition?.label ?? '';
    const nextCompare = condition?.comparison ?? '=';
    const nextValue = condition?.value ?? '';

    setLabel(nextLabel);
    setCompare(nextCompare);
    setValue(nextValue);
    setConditionOption((_conditionOptions || []).filter(opt => opt != null).find(opt => opt?.value === nextLabel) ?? null);
  }, [condition, _conditionOptions]);

  if (!conditionOption) return null;

  const updateLabel = (e) => {
    const nextLabel = e?.target?.value ?? '';
    const nextOption = (_conditionOptions || []).filter(opt => opt != null).find(opt => opt?.value === nextLabel) ?? null;

    // reset value when label changes
    const updatedRule = {
      object: nextOption?.conditionType,
      property: nextLabel,
      value: ''
    };
    dispatch(updateRuleCondition(ruleId, condition?.id, Map(updatedRule)));

    setLabel(nextLabel);
    setConditionOption(nextOption);
    setValue('');
  };

  const updateCompare = (next) => {
    const nextCompare = next ?? '=';
    setCompare(nextCompare);

    const ruleCondition = {
      comparison: nextCompare,
      object: conditionOption?.conditionType,
      property: label,
    };

    if (condition?.id) {
      dispatch(updateRuleCondition(ruleId, condition.id, Map(ruleCondition)));
    } else {
      dispatch(addRuleCondition(ruleId, Map({ ...ruleCondition, value: '' })));
    }
  };

  const validateValue = (val) => {
    if (conditionOption.validation === 'number' && val !== '' && !isNumberString(val.trim())) {
      return false;
    }
    return true;
  };

  const updateValue = (val) => {
    // normalize null/undefined → ''
    const nextVal = (val ?? '').toString();

    if (nextVal !== (condition?.value ?? '')) {
      if (conditionOption.validation && !validateValue(nextVal)) return;

      setValue(nextVal.trim());
      const updatedRule = {
        object: conditionOption.conditionType,
        property: label,
        value: nextVal.trim(),
      };
      dispatch(updateRuleCondition(ruleId, condition.id, Map(updatedRule)));
    }
  };

  const onDelete = () => {
    dispatch(deleteRuleCondition(ruleId, condition.id));
  };

  const makeHandleOrNullToggle = () => {
    dispatch(updateRuleCondition(ruleId, condition.id, Map({ or_null: !condition.or_null })));
  };

  const valueUpdatedByUser = (val) => {
    const v = val ?? '';
    if (temperatureUnit === 'F' && conditionOption.isTemp && v !== '') {
      setValue(fahrenheitToCelsiusConvertor(v).toString());
    } else {
      setValue(v);
    }
  };

  // Safe display value for the text input
  const displayValue =
    temperatureUnit === 'F' && conditionOption.isTemp
      ? (value === '' ? '' : celsiusToFahrenheitConvertor(value).toString())
      : (value ?? ''); // guarantee string


  return (
    <div className='ruleCondition'>
      <Select
        value={label ?? ''}
        variant="standard"
        onChange={updateLabel}
        style={{ width: '300px' }}
        displayEmpty
      >
        {(validConditionTypeOptions || []).map(opt => {
          if (!opt) return null;
          return (<MenuItem disabled={opt?.disabled} key={`${opt?.value}`} value={opt?.value ?? ''}>
            {opt?.label}
          </MenuItem>);
        })}
      </Select>

      <div className='comparison'>
        <ComparisonSelector
          orNull={condition.or_null}
          comparison={compare ?? '='}
          handleChange={updateCompare}
          options={conditionOption.compareOptions}
          handleOrNullToggle={makeHandleOrNullToggle}
        />
      </div>

      {conditionOption.type === 'input' ? (
        <Input
          id='input'
          type='text'
          onChange={(e) => valueUpdatedByUser(e?.target?.value)}
          onBlur={() => updateValue(value)}
          value={displayValue}
          fullWidth
          size="small"
          margin="none"
          className='input'
        />
      ) : null}

      {conditionOption.type === 'select' ? (
        <Select
          value={
            // Ensure value always matches one of the options, or fallback to ''
            ((conditionOption.options || []).find(opt => opt?.value === value)?.value) ?? ''
          }
          variant="standard"
          onChange={(e) => updateValue(e?.target?.value || "")}
          className='select'
          displayEmpty
        >
          <MenuItem value=""><em>{t('rules.condition.select')}</em></MenuItem>  {/* allow empty */}
          {((conditionOption.options || []).filter(opt => opt != null && opt?.value != null)).map((opt, idx) => {
            if (!opt || !opt?.value) return null;
            return (<MenuItem key={opt?.value ?? `opt-${idx}`} value={opt?.value ?? ''}>{opt?.label}</MenuItem>);
          })}
        </Select>
      ) : null}

      <div className='buttonRow'>
        <Button onClick={onDelete} icon={<DeleteIcon className='deleteIcon' />} />
      </div>
    </div>
  );
}

Condition.propTypes = {
  condition: PropTypes.any,
  ruleId: PropTypes.string,
  conditionTypeOptions: PropTypes.any,
  conditionOptions: PropTypes.any,
};

Condition.defaultProps = {};
